import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
    getCatalogItems,
    getSelectedCatalogItem,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getCanEditCountOfQty,
    getDisableChangeSupplierAndDeleteItems,
} from 'entities/Order/model/selectors/ordersPermissions';
import {
    getSelectedOrder,
    getSelectedOrderIsPreSubmission,
} from 'entities/Order/model/selectors/ordersSelectors';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import {
    editOrderItemSourcingActions,
    getIsSourcingInProgress,
    getIsSourcingOverridden,
    getTotalQuantity,
    getTotalSelectedPrice,
    updateTotalQuantityWithSourcing,
} from 'features/EditOrderItemSourcing';
import { RemoveItemFromOrder } from 'features/RemoveItemFromOrder';
import { getRouteForbidden } from 'shared/const/router';
import { formatNumber } from 'shared/lib/formatNumber/formatNumber';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { useUnmountEffect } from 'shared/lib/hooks/useUnmountEffect';
import { isArray } from 'shared/lib/lodash/lodash';
import { shouldShowUOM } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { OrderItemQuantityPicker } from 'shared/ui/OrderItemQuantityPicker';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';
import { Tooltip } from 'shared/ui/Tooltip';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';
import { UnitOfMeasureDisplay } from 'shared/ui/UnitOfMeasureDisplay/UnitOfMeasureDisplay';

const customSourcingTooltipContent = (t: TFunction) => {
    return t(
        // eslint-disable-next-line max-len
        "Adjusting the total quantity will overwrite the changes made to individual supplier quantities, using the property's sourcing preferences.",
    );
};

export const OrderViewItemDetailedInfo = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const item = useSelector(getSelectedCatalogItem);
    const catalogItems = useSelector(getCatalogItems);
    const canSeePrices = useSelector(getUserCanSeePrices);
    const selectedOrder = useSelector(getSelectedOrder);
    const totalQuantity = useSelector(getTotalQuantity);
    const isSourcingInProgress = useSelector(getIsSourcingInProgress);
    const totalSelectedPrice = useSelector(getTotalSelectedPrice);
    const isSourcingOverridden = useSelector(getIsSourcingOverridden);
    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
    const canEditQty = useSelector(getCanEditCountOfQty);
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);
    const disableChangeSupplierAndDeleteItems = useSelector(
        getDisableChangeSupplierAndDeleteItems,
    );

    const { openLightbox } = useImageLightbox();

    const selectedItem = item as CatalogItem | undefined;
    const items = catalogItems as CatalogItem[] | undefined;
    const showSourcingOverridden = isPreSubmission && isSourcingOverridden;
    const showPicker = isPreSubmission && canEditQty;
    const showPrices = canSeePrices && !isMaintenanceTechnician;

    // Debounced callback for total quantity updates to prevent excessive API calls to the sourcing engine
    const debounceSourcingUpdate = useDebouncedCallback(
        useCallback(
            (quantity: number) => {
                dispatch(
                    updateTotalQuantityWithSourcing({
                        quantity,
                    }),
                );
            },
            [dispatch],
        ),
    );

    const handleQuantityChange = useCallback(
        (newQuantity: number) => {
            dispatch(
                editOrderItemSourcingActions.updateTotalQuantity({
                    quantity: newQuantity,
                }),
            );

            debounceSourcingUpdate(newQuantity);
        },
        [dispatch, debounceSourcingUpdate],
    );

    useInitialEffect(() => {
        if (!selectedItem && isArray(items)) {
            let foundItem = items.find((item) => item.id === params?.itemId);

            if (foundItem) {
                dispatch(catalogItemActions.setSelectedItem(foundItem));
            } else {
                navigate(getRouteForbidden());
            }
        }
    });

    useUnmountEffect(() => {
        dispatch(catalogItemActions.setSelectedItem(null));
    });

    const totalAvailable = useMemo(
        () =>
            selectedItem?.proposals?.reduce(
                (acc, proposal) => acc + proposal.availableQuantity,
                0,
            ) || 0,
        [selectedItem],
    );

    const totalAvailableSuppliers = useMemo(
        () =>
            selectedItem?.proposals?.reduce(
                (acc, proposal) =>
                    acc + (proposal.availableQuantity > 0 ? 1 : 0),
                0,
            ) || 0,
        [selectedItem],
    );

    const isOverQuantity = useMemo(
        () => totalQuantity > totalAvailable && isPreSubmission,
        [totalQuantity, totalAvailable, isPreSubmission],
    );

    if (!selectedItem) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    gap: '20px',
                }}
            >
                <Box
                    onClick={() => openLightbox(selectedItem.thumbnailUrl)}
                    sx={{
                        width: '124px',
                        height: '124px',
                        flexGrow: 0,
                        flexShrink: 0,
                        cursor: 'pointer',
                        '&:hover': {
                            div: {
                                borderColor: '#bababa',
                            },
                        },
                    }}
                >
                    <CatalogItemImage
                        src={selectedItem.thumbnailUrl}
                        showBorder={false}
                        height="100%"
                        width="100%"
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: '12px',
                        flexGrow: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '20px',
                        }}
                    >
                        <Typography
                            component="h4"
                            typography={'poppins.h5'}
                            data-testclass="order-item-title"
                            sx={{
                                flexGrow: 1,
                            }}
                        >
                            {selectedItem.title}
                        </Typography>
                        {!disableChangeSupplierAndDeleteItems && (
                            <Box
                                sx={{
                                    flexGrow: 0,
                                    marginTop: '-4px',
                                }}
                            >
                                <RemoveItemFromOrder itemId={selectedItem.id} />
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            fontSize: '15px',
                            lineHeight: '16px',
                            color: 'rgba(0, 0, 0, 0.85)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        <Box>
                            {t('SKU')}:&nbsp;
                            <strong>{selectedItem.sku}</strong>
                        </Box>
                        {shouldShowUOM(selectedItem.uom) && (
                            <Box data-testclass="order-item-uom">
                                <UnitOfMeasureDisplay
                                    unitCode={selectedItem.uom}
                                    quantity={selectedItem.qtyPerUom}
                                ></UnitOfMeasureDisplay>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <TableContainer>
                <Table
                    sx={{
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            borderBottom: 'none',
                        },
                        '& .MuiTableBody-root .MuiTableCell-root': {
                            '&:first-of-type': {
                                borderTop: '1px solid',
                                borderBottom: '1px solid',
                                borderLeft: '1px solid',
                                borderColor: 'grey.500',
                                borderRadius: '4px 0px 0px 4px',
                            },
                            '&:last-of-type': {
                                borderTop: '1px solid',
                                borderBottom: '1px solid',
                                borderRight: '1px solid',
                                borderRadius: '0px 4px 4px 0px',
                                borderColor: 'grey.500',
                            },
                            '&': {
                                borderTop: '1px solid',
                                borderColor: 'grey.500',
                                paddingTop: '12px',
                                paddingBottom: '12px',
                            },
                        },
                        borderCollapse: 'separate',
                        borderSpacing: '0 14px',
                    }}
                    data-testid="order-view-item-detailed-info-table"
                >
                    <TableHead
                        sx={{
                            '& th': {
                                paddingTop: '0px',
                                paddingBottom: '0px',
                                lineHeight: '16px',
                            },
                        }}
                    >
                        <TableRow>
                            <TableCell>
                                <Typography
                                    typography="openSans.captionMedium"
                                    align="center"
                                >
                                    &nbsp;
                                </Typography>
                            </TableCell>
                            <TableCell
                                sx={{
                                    width: '102px',
                                    paddingLeft: '0px',
                                    paddingRight: showPrices ? '15px' : '20px',
                                }}
                            >
                                <DynamicTooltip
                                    content={customSourcingTooltipContent(t)}
                                    disabled={!showSourcingOverridden}
                                >
                                    <Typography
                                        typography="openSans.captionMedium"
                                        sx={{
                                            textAlign: showPicker
                                                ? 'center'
                                                : 'right',
                                            color: showSourcingOverridden
                                                ? '#3F6EB7'
                                                : 'inherit',
                                        }}
                                    >
                                        {showSourcingOverridden ? (
                                            <>{t('Custom total qty')}</>
                                        ) : (
                                            <>{t('Total qty')}</>
                                        )}
                                    </Typography>
                                </DynamicTooltip>
                            </TableCell>

                            {canSeePrices && (
                                <TableCell
                                    align="right"
                                    sx={{
                                        width: '100px',
                                        paddingRight: '20px',
                                        paddingLeft: '0px',
                                    }}
                                >
                                    <Typography typography="openSans.captionMedium">
                                        {t('Total')}
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow key={selectedItem.id}>
                            <TableCell
                                sx={{
                                    fontSize: '15px',
                                }}
                                data-testid="stock-availability-status"
                            >
                                {isPreSubmission && totalAvailable > 0 ? (
                                    <>
                                        <strong>
                                            {formatNumber(totalAvailable)}{' '}
                                            {t('In stock')}
                                        </strong>{' '}
                                        <span
                                            style={{
                                                color: 'rgba(0, 0, 0, 0.50)',
                                            }}
                                        >
                                            {t('at')} {totalAvailableSuppliers}{' '}
                                            {t('suppliers')}
                                        </span>
                                    </>
                                ) : (
                                    <strong>
                                        {isPreSubmission
                                            ? t('Not available')
                                            : null}
                                    </strong>
                                )}
                            </TableCell>
                            <TableCell
                                sx={{
                                    verticalAlign: 'top',
                                    width: '102px',
                                    position: 'relative',
                                    paddingLeft: '0px',
                                    paddingRight: showPrices ? '15px' : '20px',
                                }}
                                align="center"
                            >
                                {isOverQuantity && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '16px',
                                            left: '-30px',
                                        }}
                                    >
                                        <Tooltip
                                            isSecondaryColor={true}
                                            color="#FF0000"
                                            content={t(
                                                'Requested quantity for this item exceeds available stock.',
                                            )}
                                        ></Tooltip>
                                    </Box>
                                )}
                                {showPicker ? (
                                    <DynamicTooltip
                                        content={customSourcingTooltipContent(
                                            t,
                                        )}
                                        disabled={!showSourcingOverridden}
                                    >
                                        <OrderItemQuantityPicker
                                            value={totalQuantity}
                                            maxQuantity={totalAvailable}
                                            minQuantity={1}
                                            onQuantityChange={
                                                handleQuantityChange
                                            }
                                            showLoading={isSourcingInProgress}
                                            disabled={isSourcingInProgress}
                                            showCustomQuantity={
                                                showSourcingOverridden
                                            }
                                        />
                                    </DynamicTooltip>
                                ) : (
                                    <Typography sx={{ textAlign: 'right' }}>
                                        {totalQuantity}
                                    </Typography>
                                )}
                            </TableCell>

                            {showPrices && (
                                <TableCell
                                    align="right"
                                    sx={{
                                        verticalAlign: 'top',
                                        width: '100px',
                                        paddingRight: '20px',
                                        paddingLeft: '0px',
                                    }}
                                >
                                    {totalQuantity > 0 && (
                                        <OrderPriceDisplay
                                            width="55px"
                                            containerHeight="32px"
                                            containerProps={{
                                                display: 'block',
                                                lineHeight: '32px',
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                color: (theme: Theme) =>
                                                    theme.palette.common.black,
                                            }}
                                            justifyContent="flex-end"
                                            testClass="order-item-price-total"
                                            price={totalSelectedPrice}
                                            orderHasLoadingPrices={
                                                !selectedItem.isCheckFinished
                                            }
                                            order={selectedOrder}
                                        />
                                    )}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};
